/**
 * DeleteStageDialogContainer — resolves a {@link StageDeletionPlan} into the
 * copy and gating `DeleteStageDialogView` renders (SE-5488 AC2 + AC3).
 *
 * The plan (not the View) owns the branching:
 * - `blocked-has-cards` → an un-confirmable warning: the confirm is disabled and
 *   the copy tells the user to move the cards out first (AC3).
 * - `blocked-unknown-occupancy` → an un-confirmable notice while the card set is
 *   still loading: occupancy cannot be proven, so deletion is held (fail closed).
 * - `warn-rules` → a danger confirm that NAMES every affected rule and states
 *   they will be paused if deletion proceeds (AC2). When the rule set was still
 *   loading it degrades to a generic caveat rather than asserting completeness.
 * - `ready` → a plain danger confirm.
 *
 * This resolution used to run in the View behind a `useMemo`, which put a hook
 * and four branches in a file that is supposed to be a pure function of its
 * props (SE-8019).
 * @module features/player-kanban/components/molecules/DeleteStageDialog/DeleteStageDialogContainer
 */

import { useMemo } from "react";

import DeleteStageDialogView, {
  type DialogCopy,
} from "./DeleteStageDialogView";

import type {
  StageDeletionPlan,
  StageDeletionWarnRules,
} from "../../../utils/stageEditorOps";

/** Props for {@link DeleteStageDialogContainer}. */
export interface DeleteStageDialogContainerProps {
  /** Whether the dialog is visible. */
  readonly isOpen: boolean;
  /** The stage being deleted (for the copy). */
  readonly stageName: string;
  /** The resolved deletion plan — null while none is pending. */
  readonly plan: StageDeletionPlan | null;
  /** Whether the confirmed delete is in flight. */
  readonly isConfirming: boolean;
  /** Confirm handler — only reachable when the plan permits deletion. */
  readonly onConfirm: () => void;
  /** Cancel / dismiss handler. */
  readonly onClose: () => void;
}

/**
 * Copy for a `warn-rules` plan — the named-rule confirm (AC2), with a generic
 * fallback when the rule set was still loading (no complete named list yet).
 * @param stageName - The stage being deleted.
 * @param plan - The resolved `warn-rules` plan.
 * @returns The {@link DialogCopy} for the rule warning.
 */
const warnRulesCopy = (
  stageName: string,
  plan: StageDeletionWarnRules
): DialogCopy => {
  // Rule set still paginating with nothing named yet: we cannot assert the
  // stage is unreferenced, so warn generically rather than name a list.
  if (plan.rules.length === 0) {
    return {
      title: "This stage may be used by rules",
      body: [
        `The automation rules for this pipeline are still loading, so we can't confirm whether any reference “${stageName}”.`,
        "Any that do will be paused if you delete the stage.",
      ],
      confirmLabel: "Delete stage",
      cancelLabel: "Cancel",
      tone: "danger",
      isConfirmDisabled: false,
    };
  }

  // AC2: name every affected rule and state they will be paused on delete.
  const ruleNames = plan.rules.map(rule => `• ${rule.name}`);
  const caveat = plan.mayHaveMoreRules
    ? ["Other rules are still loading and may also reference this stage."]
    : [];
  return {
    title: "This stage is used by rules",
    body: [
      `Deleting “${stageName}” affects ${plan.rules.length} rule${
        plan.rules.length === 1 ? "" : "s"
      }:`,
      ...ruleNames,
      ...caveat,
      "These rules will be paused if you delete the stage.",
    ],
    confirmLabel: "Pause rules & delete",
    cancelLabel: "Cancel",
    tone: "danger",
    isConfirmDisabled: false,
  };
};

/**
 * Derive the dialog copy + gating from the deletion plan.
 * @param stageName - The stage being deleted.
 * @param plan - The resolved plan (null falls back to a neutral confirm).
 * @returns The {@link DialogCopy} for the current plan.
 */
const copyForPlan = (
  stageName: string,
  plan: StageDeletionPlan | null
): DialogCopy => {
  if (plan?.status === "blocked-has-cards") {
    // AC3: deletion is BLOCKED until the stage is empty — the confirm stays
    // disabled and the copy tells the user what to do instead.
    const noun = plan.cardCount === 1 ? "card" : "cards";
    return {
      title: "Move cards first",
      body: [
        `“${stageName}” still has ${plan.cardCount} ${noun}.`,
        "Move the cards to another stage before deleting this one. Deletion is blocked until the stage is empty.",
      ],
      confirmLabel: "Delete stage",
      cancelLabel: "Close",
      tone: "warning",
      isConfirmDisabled: true,
    };
  }

  if (plan?.status === "blocked-unknown-occupancy") {
    // Fail closed: the card set is still loading, so occupancy is unproven and
    // deletion is held until the count is authoritative.
    return {
      title: "Checking the stage",
      body: [
        "This pipeline's cards are still loading, so we can't yet confirm this stage is empty.",
        "Try again in a moment — deletion is held until we can be sure no cards would be lost.",
      ],
      confirmLabel: "Delete stage",
      cancelLabel: "Close",
      tone: "warning",
      isConfirmDisabled: true,
    };
  }

  if (plan?.status === "warn-rules") {
    return warnRulesCopy(stageName, plan);
  }

  return {
    title: "Delete stage",
    body: [`Delete “${stageName}”?`, "This action cannot be undone."],
    confirmLabel: "Delete stage",
    cancelLabel: "Cancel",
    tone: "danger",
    isConfirmDisabled: false,
  };
};

/**
 * Resolve the plan into copy and render the dialog.
 * @param props - {@link DeleteStageDialogContainerProps}.
 * @param props.isOpen - Whether the dialog is visible.
 * @param props.stageName - The stage being deleted.
 * @param props.plan - The resolved deletion plan.
 * @param props.isConfirming - Whether the confirmed delete is in flight.
 * @param props.onConfirm - Confirm handler.
 * @param props.onClose - Cancel / dismiss handler.
 * @returns The confirm dialog.
 */
function DeleteStageDialogContainer({
  isOpen,
  stageName,
  plan,
  isConfirming,
  onConfirm,
  onClose,
}: DeleteStageDialogContainerProps) {
  const copy = useMemo(() => copyForPlan(stageName, plan), [stageName, plan]);

  return (
    <DeleteStageDialogView
      isOpen={isOpen}
      copy={copy}
      isConfirming={isConfirming}
      onConfirm={onConfirm}
      onClose={onClose}
    />
  );
}

export default DeleteStageDialogContainer;
